import React, { FC, useContext, useState } from 'react';
import styled from 'styled-components';
import { Button, Col, Form, Row } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import AntDesignStyleOverrides from '../../core/components/App/AntDesignStyleOverrides';
import { InputLocation } from './InputLocation';
import { Profile } from '../hooks/useGetConsumerSchedulingProfileQuery';
import { SelectPayorAndPlan } from './SelectPayorAndPlan';
import { SelectSpecialtyAndProcedure } from './SelectSpecialtyAndProcedure';
import { ConsumerSchedulingContext } from '../contexts/ConsumerSchedulingContext';

export interface ProfileSearchFormData {
  location: string;
  payorId?: string;
  payorPlanId?: string;
  procedureId: string;
  specialtyId: string;
}

interface Props extends FormComponentProps {
  form: WrappedFormUtils<ProfileSearchFormData>;
  initialLocation?: string;
  initialPayorId?: string;
  initialPayorPlanId?: string;
  initialProcedureId?: string;
  initialSpecialtyId?: string;
  onSubmit: (values: ProfileSearchFormData) => void;
  profile: Profile;
  setLatitude: (latitude: number | undefined) => void;
  setLongitude: (longitude: number | undefined) => void;
  embedded: boolean;
}

const Styles = styled.div`
  .search-button {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    font-family: Source Sans Pro, sans-serif;
    font-size: 14px;
    height: 38px;
    text-align: center;
    text-decoration: none solid;
    text-transform: uppercase;
    width: 100%;
  }
`;

const ProfileSearchFormComponent: FC<Props> = ({
  form,
  initialLocation,
  initialPayorId,
  initialPayorPlanId,
  initialProcedureId,
  initialSpecialtyId,
  onSubmit,
  profile,
  setLatitude,
  setLongitude,
  embedded,
}): JSX.Element => {
  const { organization } = useContext(ConsumerSchedulingContext);
  const [disabled, setDisabled] = useState<boolean>(false);

  const handleProfileSearchFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onSubmit(values);
      }
    });
  };

  return (
    <AntDesignStyleOverrides>
      <Styles>
        <Form onSubmit={handleProfileSearchFormSubmit}>
          <Row gutter={[24, 0]}>
            <Col xs={24} lg={8}>
              <SelectSpecialtyAndProcedure
                form={form}
                initialProcedureId={initialProcedureId}
                initialSpecialtyId={initialSpecialtyId}
                specialties={profile.specialties}
              />
            </Col>
            {organization.consumerSchedulingSettings === null || organization.consumerSchedulingSettings?.showMap ? (
              <Col xs={24} lg={8}>
                <InputLocation
                  form={form}
                  initialLocation={initialLocation}
                  setLatitude={setLatitude}
                  setLongitude={setLongitude}
                  embedded={embedded}
                  onLocationChange={valid => setDisabled(!valid)}
                />
              </Col>
            ) : null}
            <Col xs={24} lg={8}>
              {organization.consumerSchedulingSettings === null ||
              organization.consumerSchedulingSettings?.searchInsurance ? (
                <SelectPayorAndPlan
                  form={form}
                  initialPayorId={initialPayorId}
                  initialPayorPlanId={initialPayorPlanId}
                />
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Button className="search-button" type="primary" htmlType="submit" disabled={disabled}>
                Search
              </Button>
            </Col>
          </Row>
        </Form>
      </Styles>
    </AntDesignStyleOverrides>
  );
};

export const ProfileSearchForm = Form.create<Props>()(ProfileSearchFormComponent);
